const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const UPLOADS_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
const TOKEN_KEY = 'tarjemlek_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  let url = `${UPLOADS_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
  const token = getToken();
  if (token && pathOrUrl.startsWith('/uploads/')) {
    url += `${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  }
  return url;
}

export async function api(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const error = new Error(data?.error || 'requestFailed');
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const contactApi = {
  send: (payload) =>
    api('/contact', { method: 'POST', body: JSON.stringify(payload) }),
};

export const authApi = {
  login: (email, password) =>
    api('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (payload) =>
    api('/auth/register', { method: 'POST', body: JSON.stringify(payload) }),
  registerTranslator: (payload) =>
    api('/auth/register/translator', { method: 'POST', body: JSON.stringify(payload) }),
  checkEmail: (email) => api(`/auth/check-email?email=${encodeURIComponent(email)}`),
  me: () => api('/auth/me'),
  updateMe: (patch) =>
    api('/auth/me', { method: 'PATCH', body: JSON.stringify(patch) }),
  changePassword: (payload) =>
    api('/auth/change-password', { method: 'POST', body: JSON.stringify(payload) }),
  forgotPassword: (email) =>
    api('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) =>
    api('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  claim: (email) =>
    api('/auth/claim', { method: 'POST', body: JSON.stringify({ email }) }),
  claimConfirm: (token, password) =>
    api('/auth/claim/confirm', { method: 'POST', body: JSON.stringify({ token, password }) }),
  uploadFile: (file) => {
    const body = new FormData();
    body.append('file', file);
    return api('/auth/upload', { method: 'POST', body });
  },
};

export const publicApi = {
  settings: () => api('/auth/settings/public'),
  catalog: () => api('/catalog'),
  subServices: (parent) => api(`/catalog/sub-services${parent ? `?parent=${encodeURIComponent(parent)}` : ''}`),
};

export const clientApi = {
  overview: () => api('/client/overview'),
  orders: () => api('/client/orders'),
  order: (id) => api(`/client/orders/${id}`),
  quotes: () => api('/client/quotes'),
  createOrder: (payload, files = []) => {
    const body = new FormData();
    body.append('data', JSON.stringify(payload));
    files.forEach((f) => body.append('files', f));
    return api('/client/orders', { method: 'POST', body });
  },
  createQuote: (payload) =>
    api('/client/quotes', { method: 'POST', body: JSON.stringify(payload) }),
  respondQuote: (id, decision) =>
    api(`/client/quotes/${id}/respond`, { method: 'PATCH', body: JSON.stringify({ decision }) }),
  invoices: () => api('/client/invoices'),
  messages: (threadKey) => api(`/client/messages${threadKey ? `?threadKey=${encodeURIComponent(threadKey)}` : ''}`),
  sendMessage: (payload) =>
    api('/client/messages', { method: 'POST', body: JSON.stringify(payload) }),
  notifications: () => api('/client/notifications'),
  readNotifications: (filter) =>
    api('/client/notifications/read', {
      method: 'PATCH',
      body: JSON.stringify(typeof filter === 'string' ? { link: filter } : filter || {}),
    }),
  payments: () => api('/client/payments'),
  documents: () => api('/client/documents'),
  createPayment: (payload) =>
    api('/client/payments', { method: 'POST', body: JSON.stringify(payload) }),
  submitReview: (orderId, payload) =>
    api(`/client/orders/${orderId}/review`, { method: 'POST', body: JSON.stringify(payload) }),
  updateReview: (id, payload) =>
    api(`/client/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
};

export const translatorApi = {
  overview: () => api('/translator/overview'),
  missions: () => api('/translator/missions'),
  mission: (id) => api(`/translator/missions/${id}`),
  updateMissionStatus: (id, status, notes) =>
    api(`/translator/missions/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    }),
  respondMission: (id, decision, translatorNote = '') =>
    api(`/translator/missions/${id}/respond`, {
      method: 'PATCH',
      body: JSON.stringify({ decision, translatorNote }),
    }),
  withdrawMission: (id, reason) =>
    api(`/translator/missions/${id}/withdraw`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    }),
  uploadDocuments: (id, files, kind = 'final') => {
    const body = new FormData();
    body.append('kind', kind);
    Array.from(files).forEach((f) => body.append('files', f));
    return api(`/translator/missions/${id}/documents`, { method: 'POST', body });
  },
  documents: () => api('/translator/documents'),
  profile: () => api('/translator/profile'),
  updateProfile: (patch) =>
    api('/translator/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
  notifications: () => api('/translator/notifications'),
  readNotifications: (filter) =>
    api('/translator/notifications/read', {
      method: 'PATCH',
      body: JSON.stringify(typeof filter === 'string' ? { link: filter } : filter || {}),
    }),
  messages: () => api('/translator/messages'),
  sendMessage: (payload) =>
    api('/translator/messages', { method: 'POST', body: JSON.stringify(payload) }),
  requestVerification: (id, notes = '') =>
    api(`/translator/missions/${id}/request-verification`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }),
  reviews: () => api('/translator/reviews'),
  flagReview: (id, reason) =>
    api(`/translator/reviews/${id}/flag`, { method: 'POST', body: JSON.stringify({ reason }) }),
};

export const verifierApi = {
  overview: () => api('/verifier/overview'),
  verifications: (status) => api(`/verifier/verifications${status ? `?status=${status}` : ''}`),
  verification: (id) => api(`/verifier/verifications/${id}`),
  updateStatus: (id, status, reason, checklist) =>
    api(`/verifier/verifications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason, checklist }),
    }),
  addComment: (id, body) =>
    api(`/verifier/verifications/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  profile: () => api('/verifier/profile'),
  updateProfile: (patch) =>
    api('/verifier/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
  notifications: () => api('/verifier/notifications'),
  readNotifications: (filter) =>
    api('/verifier/notifications/read', {
      method: 'PATCH',
      body: JSON.stringify(typeof filter === 'string' ? { link: filter } : filter || {}),
    }),
  messages: () => api('/verifier/messages'),
  sendMessage: (payload) =>
    api('/verifier/messages', { method: 'POST', body: JSON.stringify(payload) }),
};

export const adminApi = {
  overview: () => api('/admin/overview'),
  orders: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.set(k, v);
    });
    const query = qs.toString();
    return api(`/admin/orders${query ? `?${query}` : ''}`);
  },
  order: (id) => api(`/admin/orders/${id}`),
  updateOrderStatus: (id, status, extra = {}) =>
    api(`/admin/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, ...extra }),
    }),
  updateOrderMeta: (id, patch) =>
    api(`/admin/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  assignOrder: (id, translatorId, notes = '') =>
    api(`/admin/orders/${id}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ translatorId, notes }),
    }),
  orderCandidates: (id) => api(`/admin/orders/${id}/candidates`),
  suggestOrder: (id) => api(`/admin/orders/${id}/suggest`, { method: 'POST', body: '{}' }),
  acceptSuggestion: (id, notes = '') =>
    api(`/admin/orders/${id}/accept-suggestion`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }),
  unassignOrder: (id) =>
    api(`/admin/orders/${id}/unassign`, { method: 'PATCH', body: '{}' }),
  assignments: () => api('/admin/assignments'),
  uploadOrderDocument: (id, file, kind = 'source') => {
    const body = new FormData();
    body.append('file', file);
    body.append('kind', kind);
    return api(`/admin/orders/${id}/documents`, { method: 'POST', body });
  },
  assignVerifier: (id, verifierId, notes = '') =>
    api(`/admin/orders/${id}/assign-verifier`, {
      method: 'PATCH',
      body: JSON.stringify({ verifierId, notes }),
    }),
  users: () => api('/admin/users'),
  clientDetail: (id) => api(`/admin/clients/${id}`),
  updateUser: (id, patch) =>
    api(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  setUserActive: (id, isActive) =>
    api(`/admin/users/${id}/active`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
  translators: () => api('/admin/translators'),
  getTranslator: (id) => api(`/admin/translators/${id}`),
  createTranslator: (payload) =>
    api('/admin/translators', { method: 'POST', body: JSON.stringify(payload) }),
  updateTranslator: (id, payload) =>
    api(`/admin/translators/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  missions: () => api('/admin/missions'),
  quotes: () => api('/admin/quotes'),
  updateQuote: (id, payload) =>
    api(`/admin/quotes/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  createQuote: (payload) =>
    api('/admin/quotes', { method: 'POST', body: JSON.stringify(payload) }),
  languages: () => api('/admin/languages'),
  importLanguages: (activate = true) =>
    api('/admin/languages/import', { method: 'POST', body: JSON.stringify({ activate }) }),
  createLanguage: (payload) =>
    api('/admin/languages', { method: 'POST', body: JSON.stringify(payload) }),
  updateLanguage: (id, payload) =>
    api(`/admin/languages/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteLanguage: (id) => api(`/admin/languages/${id}`, { method: 'DELETE' }),
  services: () => api('/admin/services'),
  createService: (payload) =>
    api('/admin/services', { method: 'POST', body: JSON.stringify(payload) }),
  updateService: (id, payload) =>
    api(`/admin/services/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteService: (id) => api(`/admin/services/${id}`, { method: 'DELETE' }),
  catalogTypes: () => api('/admin/types'),
  createCatalogType: (payload) =>
    api('/admin/types', { method: 'POST', body: JSON.stringify(payload) }),
  updateCatalogType: (id, payload) =>
    api(`/admin/types/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteCatalogType: (id) => api(`/admin/types/${id}`, { method: 'DELETE' }),
  seedCatalogTypes: () => api('/admin/types/seed', { method: 'POST', body: JSON.stringify({}) }),
  seedCatalogServices: () => api('/admin/services/seed', { method: 'POST', body: JSON.stringify({}) }),
  deliveryOptions: () => api('/admin/delivery'),
  seedDeliveryOptions: () => api('/admin/delivery/seed', { method: 'POST', body: JSON.stringify({}) }),
  createDeliveryOption: (payload) =>
    api('/admin/delivery', { method: 'POST', body: JSON.stringify(payload) }),
  updateDeliveryOption: (id, payload) =>
    api(`/admin/delivery/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteDeliveryOption: (id) => api(`/admin/delivery/${id}`, { method: 'DELETE' }),
  invoices: () => api('/admin/invoices'),
  createVerifier: (payload) =>
    api('/admin/verifiers', { method: 'POST', body: JSON.stringify(payload) }),
  verifiers: () => api('/admin/verifiers'),
  getVerifier: (id) => api(`/admin/verifiers/${id}`),
  updateVerifier: (id, payload) =>
    api(`/admin/verifiers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  updateVerifierStatus: (id, { isActive, availability }) =>
    api(`/admin/verifiers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive, availability }),
    }),
  reviews: () => api('/admin/reviews'),
  createReview: (payload) =>
    api('/admin/reviews', { method: 'POST', body: JSON.stringify(payload) }),
  clientReviews: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.set(k, v);
    });
    const query = qs.toString();
    return api(`/admin/client-reviews${query ? `?${query}` : ''}`);
  },
  moderateClientReview: (id, payload) =>
    api(`/admin/client-reviews/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteClientReview: (id, reason) =>
    api(`/admin/client-reviews/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  payments: () => api('/admin/payments'),
  confirmPayment: (id) =>
    api(`/admin/payments/${id}/confirm`, { method: 'POST' }),
  rejectPayment: (id, reason) =>
    api(`/admin/payments/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  createInvoice: (payload) =>
    api('/admin/invoices', { method: 'POST', body: JSON.stringify(payload) }),
  updateInvoice: (id, payload) =>
    api(`/admin/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  uploadFile: (file) => {
    const body = new FormData();
    body.append('file', file);
    return api('/admin/upload', { method: 'POST', body });
  },
  messages: (threadKey) => api(`/admin/messages${threadKey ? `?threadKey=${encodeURIComponent(threadKey)}` : ''}`),
  notifications: () => api('/admin/notifications'),
  readNotifications: (filter) =>
    api('/admin/notifications/read', {
      method: 'PATCH',
      body: JSON.stringify(typeof filter === 'string' ? { link: filter } : filter || {}),
    }),
  sendMessage: (payload) =>
    api('/admin/messages', { method: 'POST', body: JSON.stringify(payload) }),
  settings: () => api('/admin/settings'),
  updateSettings: (settings) =>
    api('/admin/settings', { method: 'POST', body: JSON.stringify({ settings }) }),
  emailStatus: () => api('/admin/email/status'),
  testEmail: (to) =>
    api('/admin/email/test', { method: 'POST', body: JSON.stringify({ to }) }),
  admins: () => api('/admin/admins'),
  createAdmin: (payload) =>
    api('/admin/admins', { method: 'POST', body: JSON.stringify(payload) }),
  updateAdmin: (id, payload) =>
    api(`/admin/admins/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  emailLogs: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v)
    ).toString();
    return api(`/admin/email-logs${qs ? `?${qs}` : ''}`);
  },
  emailLog: (id) => api(`/admin/email-logs/${id}`),
};

export const orderApi = {
  // Vérifie si un professionnel couvre la paire de langues demandée.
  checkPair: ({ source, target, category = 'translation', translationType = '' }) =>
    api(`/orders/check-pair?source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}&category=${category}&translationType=${translationType}`),
  // Compte les mots / pages facturables des fichiers avant la commande.
  analyzeFiles: (files = []) => {
    const body = new FormData();
    files.forEach((f) => body.append('files', f));
    return api('/orders/analyze', { method: 'POST', body });
  },
  create: (payload, files = []) => {
    const body = new FormData();
    body.append('data', JSON.stringify(payload));
    files.forEach((f) => body.append('files', f));
    return api('/orders', { method: 'POST', body });
  },
  pay: (code, payload) =>
    api(`/orders/${encodeURIComponent(code)}/pay`, { method: 'POST', body: JSON.stringify(payload) }),
  track: (code, token) =>
    api(`/orders/track/${encodeURIComponent(code)}?token=${encodeURIComponent(token || '')}`),
};

export const directoryApi = {
  professionals: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.set(k, v);
    });
    const query = qs.toString();
    return api(`/directory/professionals${query ? `?${query}` : ''}`);
  },
  professional: (id) => api(`/directory/professionals/${id}`),
  // Témoignages publiés. `params.type` ou `params.service` limite aux avis
  // liés aux sous-services d'un type / à un sous-service précis.
  testimonials: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.service) qs.set('service', params.service);
    const q = qs.toString();
    return api(`/directory/testimonials${q ? `?${q}` : ''}`);
  },
};
